/**
 * Home-grown procedural Web Audio synth — `SfxEngine`'s sound source. Ported from
 * `@rexa-developer/tiks` v0.3.0's synthesis core (MIT), which is itself deterministic: same theme
 * in, byte-identical Web Audio graph out, every time. **That flatness is the problem this port
 * fixes** — a `click()` fired forty times in a session sounds like a single sample looping. Every
 * generator below re-rolls its pitch/timing/gain a few percent on each call (see `jitter()`), so
 * repeated triggers vary audibly without reading as a different sound.
 *
 * Only the "soft" theme's constants are ported (nothing in this app switches themes), and only
 * the three generators `SfxEngine` uses: `click`/`error`/`swoosh`.
 */

const AudioContextCtor: typeof AudioContext | undefined =
  typeof window !== "undefined"
    ? (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)
    : undefined;

const GESTURE_EVENTS = ["pointerdown", "touchstart", "mousedown", "keydown"] as const;

// "soft" theme constants (tiks's default) — baked in rather than a swappable theme object,
// since nothing in this app switches themes.
const BASE_FREQ = 440;
const FILTER_FREQ = 3000;
const FILTER_Q = 2;
const ATTACK = 0.002;
const DECAY = 1;

const NOISE_DURATION_S = 0.5;
const SCHEDULE_LATENCY_S = 0.005;

/** ±`pct` fractional jitter around `v` (e.g. `jitter(100, 0.05)` → a value in `[95, 105]`). */
function jitter(v: number, pct: number): number {
  return v * (1 + (Math.random() * 2 - 1) * pct);
}

let pinkNoiseBuffer: AudioBuffer | null = null;
let pinkNoiseBufferCtx: AudioContext | null = null;

/** Pink noise via the Paul Kellet filter cascade — same coefficients tiks uses. Cached per-ctx. */
function getPinkNoiseBuffer(ctx: AudioContext): AudioBuffer {
  if (pinkNoiseBufferCtx === ctx && pinkNoiseBuffer) return pinkNoiseBuffer;
  const frames = Math.floor(ctx.sampleRate * NOISE_DURATION_S);
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;
  for (let i = 0; i < frames; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.969 * b2 + white * 0.153852;
    b3 = 0.8665 * b3 + white * 0.3104856;
    b4 = 0.55 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.016898;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }
  pinkNoiseBuffer = buffer;
  pinkNoiseBufferCtx = ctx;
  return buffer;
}

function noiseSource(ctx: AudioContext): AudioBufferSourceNode {
  const src = ctx.createBufferSource();
  src.buffer = getPinkNoiseBuffer(ctx);
  src.loop = true;
  return src;
}

function scheduleStart(ctx: AudioContext): number {
  return ctx.currentTime + SCHEDULE_LATENCY_S;
}

/** Short bandpass noise transient — a physical "tap." */
function synthClick(ctx: AudioContext, dest: AudioNode): void {
  const t = scheduleStart(ctx);
  const decay = Math.max(0.013 * jitter(DECAY, 0.15), 0.005);
  const src = noiseSource(ctx);
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = jitter(FILTER_FREQ, 0.08);
  filter.Q.value = jitter(FILTER_Q, 0.1);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.001, t);
  gain.gain.linearRampToValueAtTime(jitter(0.9, 0.1), t + Math.min(ATTACK, decay * 0.5));
  gain.gain.exponentialRampToValueAtTime(0.001, t + decay);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(dest);
  src.start(t);
  src.stop(t + decay);
}

/** Falling-pitch tone plus a low thump — used for `impact()`. */
function synthError(ctx: AudioContext, dest: AudioNode): void {
  const t = scheduleStart(ctx);
  const ratio = jitter(BASE_FREQ, 0.03) / 440;
  const decay = Math.max(0.15 * jitter(DECAY, 0.15), 0.005);
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(jitter(280, 0.05) * ratio, t);
  osc.frequency.exponentialRampToValueAtTime(jitter(180, 0.05) * ratio, t + decay);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.001, t);
  gain.gain.linearRampToValueAtTime(jitter(0.75, 0.1), t + ATTACK);
  gain.gain.exponentialRampToValueAtTime(0.001, t + decay);
  osc.connect(gain);
  gain.connect(dest);
  osc.start(t);
  osc.stop(t + decay);

  const thumpDecay = Math.max(0.1 * jitter(DECAY, 0.15), 0.005);
  const noise = noiseSource(ctx);
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = jitter(600, 0.1);
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(jitter(0.45, 0.1), t);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, t + thumpDecay);
  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(dest);
  noise.start(t);
  noise.stop(t + thumpDecay);
}

/** Bandpass sweep over noise, high to low — used for `miss()`. */
function synthSwoosh(ctx: AudioContext, dest: AudioNode): void {
  const t = scheduleStart(ctx);
  const decay = Math.max(0.12 * jitter(DECAY, 0.15), 0.005);
  const src = noiseSource(ctx);
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.Q.value = jitter(FILTER_Q, 0.1);
  const startFreq = jitter(FILTER_FREQ * 2, 0.1);
  const endFreq = Math.max(jitter(FILTER_FREQ * 0.3, 0.1), 20);
  filter.frequency.setValueAtTime(startFreq, t);
  filter.frequency.exponentialRampToValueAtTime(endFreq, t + decay);
  const rampTime = Math.min(ATTACK * 2, decay * 0.1);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.001, t);
  gain.gain.linearRampToValueAtTime(jitter(0.6, 0.1), t + rampTime);
  gain.gain.exponentialRampToValueAtTime(0.001, t + decay);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(dest);
  src.start(t);
  src.stop(t + decay);
}

type SoundGenerator = (ctx: AudioContext, dest: AudioNode) => void;

type SynthOptions = {
  muted?: boolean;
  volume?: number;
};

class Synth {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private muted = false;
  private reducedMotionPreferred = false;
  private volume = 0.3;
  private lifecycleBound = false;
  private unlockBound = false;
  private reducedMotionBound = false;
  private unlockTeardown: (() => void) | null = null;

  init(options: SynthOptions = {}): void {
    if (!AudioContextCtor) return;
    if (options.volume !== undefined) {
      this.volume = Math.max(0, Math.min(1, options.volume));
      if (this.masterGain) this.masterGain.gain.value = this.volume;
    }
    if (options.muted !== undefined) this.muted = options.muted;
    this.bindReducedMotion();
    this.bindLifecycle();
    this.bindGestureUnlock();
  }

  private createContext(): AudioContext | null {
    if (!AudioContextCtor) return null;
    if (this.ctx && this.ctx.state !== "closed") return this.ctx;
    this.ctx = new AudioContextCtor();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.volume;
    this.limiter = this.ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -3;
    this.limiter.knee.value = 0;
    this.limiter.ratio.value = 20;
    this.limiter.attack.value = 0.003;
    this.limiter.release.value = 0.1;
    this.masterGain.connect(this.limiter);
    this.limiter.connect(this.ctx.destination);
    return this.ctx;
  }

  private bindReducedMotion(): void {
    if (this.reducedMotionBound || typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    this.reducedMotionBound = true;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      this.reducedMotionPreferred = query.matches;
    };
    sync();
    query.addEventListener("change", sync);
  }

  private bindLifecycle(): void {
    if (this.lifecycleBound || typeof window === "undefined" || typeof document === "undefined") return;
    this.lifecycleBound = true;
    const resume = () => {
      const ctx = this.ctx;
      if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    };
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") resume();
    });
    window.addEventListener("pageshow", resume);
  }

  /** Create the `AudioContext` on the first real user gesture — never eagerly. */
  private bindGestureUnlock(): void {
    if (this.unlockBound || typeof document === "undefined") return;
    this.unlockBound = true;
    let unlocked = false;
    const onGesture = () => {
      if (unlocked) return;
      unlocked = true;
      const ctx = this.createContext();
      if (!ctx) return;
      if (ctx.state === "suspended") {
        ctx.resume().then(
          () => {
            if (ctx.state === "running") this.unlockTeardown?.();
            else unlocked = false;
          },
          () => {
            unlocked = false;
          },
        );
      }
      try {
        const silentSource = ctx.createBufferSource();
        silentSource.buffer = ctx.createBuffer(1, 1, 22050);
        silentSource.connect(ctx.destination);
        silentSource.start(0);
      } catch {
        /* ignore — a browser without buffer-source support has bigger problems */
      }
      if (ctx.state === "running") this.unlockTeardown?.();
    };
    const listenerOptions: AddEventListenerOptions = { capture: true, passive: true };
    for (const event of GESTURE_EVENTS) document.addEventListener(event, onGesture, listenerOptions);
    this.unlockTeardown = () => {
      for (const event of GESTURE_EVENTS) document.removeEventListener(event, onGesture, listenerOptions);
      this.unlockTeardown = null;
    };
  }

  private isSilenced(): boolean {
    return this.muted || this.reducedMotionPreferred;
  }

  private play(generator: SoundGenerator): void {
    if (this.isSilenced()) return;
    const ctx = this.ctx;
    const dest = this.masterGain;
    if (!ctx || !dest) return;
    if (ctx.state === "suspended") {
      ctx.resume().then(() => {
        if (ctx.state === "running") generator(ctx, dest);
      }, () => {});
      return;
    }
    generator(ctx, dest);
  }

  click(): void {
    this.play(synthClick);
  }

  error(): void {
    this.play(synthError);
  }

  swoosh(): void {
    this.play(synthSwoosh);
  }

  mute(): void {
    this.muted = true;
  }

  unmute(): void {
    this.muted = false;
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.masterGain) this.masterGain.gain.value = this.volume;
  }
}

/** The one `Synth` instance — `SfxEngine` is the only intended caller. */
export const synth = new Synth();
